/** KPI карточки поставки: начисления за всё время (из `/summary` со scope на всём диапазоне
 *  витрины), штуки план/факт, кросс-док ₽/шт и расхождения (из детали `/supplies/{n}` — по всем
 *  поставкам заявки, за всё время). */
import type { OzSummary, OzSupplyDetail } from "@/api/ozonAnalytics";
import { xdPerUnit, xdPerUnitLabel } from "./calc";
import { fmtDate, int } from "./format";
import { moneyKpi, type Kpi } from "./kpi";

const plain = (title: string, cur: string, curTitle: string | null, hint: string): Kpi => ({
  title,
  cur,
  curTitle,
  was: null,
  wasTitle: null,
  deltaAbs: null,
  deltaRel: null,
  tone: "neutral",
  hint,
});

/** Начисления по поставке за ВСЁ время витрины (кросс-докинг, обработка, ошибки поставок) —
 *  расходом, без сравнения: начисления разовые и приходят месяцами позже отгрузки, Δ к прошлому
 *  периоду — шум. Выручки у поставки нет, поэтому сумма = удержания сводки. `s` — сводка со scope
 *  поставки на диапазоне `range` (данные витрины). */
export function supplyChargesKpi(s: OzSummary, range: { from: string; to: string }): Kpi {
  return moneyKpi(
    "Начисления за всё время",
    "expense",
    s.totals.cur.deductions,
    undefined,
    `Начисления Ozon по заявке за всё время данных витрины (${fmtDate(range.from)}–${fmtDate(range.to)}): кросс-докинг, обработка, ошибки поставок`,
  );
}

/** Сумма по поставкам заявки; хоть у одной поставки нет значения — null (сумма была бы занижена). */
function sumAll(values: (number | null)[]): number | null {
  if (values.length === 0 || values.some((v) => v === null)) return null;
  return (values as number[]).reduce((a, b) => a + b, 0);
}

/** План / факт штук заявки: план — состав поставок, факт — принято + брак по актам. */
export function supplyUnitsKpi(d: OzSupplyDetail): Kpi {
  const plan = sumAll(d.supplies.map((s) => s.plan_units));
  const fact = sumAll(d.supplies.map((s) => s.fact_units));
  const t = (v: number | null): string => (v === null ? "—" : int(v));
  return plain(
    "Штук: план / факт",
    `${t(plan)} / ${t(fact)}`,
    d.supplies.length === 0
      ? "Заявки нет в диагнозе поставок — штук нет"
      : fact === null
        ? "Нет акта приёмки хотя бы по одной поставке заявки — факт не суммируем"
        : null,
    "план — состав поставок заявки; факт — принято + брак по актам приёмки",
  );
}

/** Кросс-докинг заявки за всё время / факт штук — та же база, что в списке «по дате отправки». */
export function supplyXdKpi(d: OzSupplyDetail): Kpi {
  const xd = d.accruals.filter((a) => a.grp === "xd").reduce((a, b) => a + b.amount, 0);
  const fact = sumAll(d.supplies.map((s) => s.fact_units));
  const x = xdPerUnit(
    xd,
    fact,
    d.supplies.map((s) => s.fact_units),
  );
  const hint = "кросс-докинг заявки за всё время / принятые штуки (факт по акту)";
  if (x === null) return plain("Кросс-док, ₽/шт", "—", "Кросс-докинга по заявке нет", hint);
  const { text, title } = xdPerUnitLabel(x, "ship");
  return plain("Кросс-док, ₽/шт", text, title, hint);
}

/** Расхождение факт − план по заявке: недовоз (меньше плана) / излишки (больше). Пересорт внутри
 *  заявки (недовоз одного артикула = излишек другого) итог не меняет — он в разборе и в акте. */
export function supplyDiffKpi(d: OzSupplyDetail): Kpi {
  const plan = sumAll(d.supplies.map((s) => s.plan_units));
  const fact = sumAll(d.supplies.map((s) => s.fact_units));
  const hint =
    "факт (принято + брак) против плана заявки; пересорт внутри заявки итог не меняет — см. «Состав и расхождения»";
  if (plan === null || fact === null) {
    return plain(
      "Расхождения",
      "—",
      d.supplies.length === 0
        ? "Заявки нет в диагнозе поставок"
        : "Нет плана или факта хотя бы по одной поставке заявки",
      hint,
    );
  }
  const diff = fact - plan;
  const cur = diff < 0 ? `недовоз ${int(-diff)} шт.` : diff > 0 ? `излишки ${int(diff)} шт.` : "нет";
  return plain("Расхождения", cur, null, hint);
}
